/**
 * 远程日志上报工具
 * 将客户端日志发送到后端 API，方便在移动设备上调试
 */

interface LogEntry {
  level: 'log' | 'warn' | 'error' | 'info';
  message: string;
  data?: any;
  timestamp: string;
  url: string;
  userAgent: string;
  tenantId?: string;
  lineUserId?: string;
}

class RemoteLogger {
  private queue: LogEntry[] = [];
  private isSending = false;
  private maxQueueSize = 10; // 队列大小，达到此数量时立即发送
  private debounceTimer: number | null = null; // 防抖計時器，用於延遲發送
  private flushOnUnload = false;
  private currentTenantId: string | null = null; // 當前設定的 tenantId（優先於 URL）
  /** 原始 console.error，用於上報失敗時只輸出到主控台、不再次觸發遠端上報 */
  private originalConsoleError: typeof console.error | null = null;

  setOriginalConsoleError(fn: typeof console.error) {
    this.originalConsoleError = fn;
  }

  /** 取得原始 console.error，用於僅輸出主控台、不寫入遠端 queue（例如重複錯誤去重） */
  getOriginalConsoleError(): typeof console.error | null {
    return this.originalConsoleError;
  }

  // 設置當前的 tenantId（用於 LINE Pay 返回頁面等場景）
  setTenantId(tenantId: string | null) {
    this.currentTenantId = tenantId;
  }

  private async sendLogs() {
    if (this.isSending || this.queue.length === 0) return;

    // 清除防抖計時器（如果有的話）
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    this.isSending = true;
    const logsToSend = [...this.queue];
    this.queue = [];

    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL;
      if (!apiBase) {
        console.warn('[RemoteLogger] VITE_API_BASE_URL 未設定，無法上報日誌');
        return;
      }

      // 優先使用設定的 tenantId，其次從日誌條目中獲取，最後從 URL 獲取
      let tenantId = this.currentTenantId;
      if (!tenantId && logsToSend.length > 0) {
        // 從日誌條目中獲取 tenantId（取第一個非空的）
        tenantId = logsToSend.find(log => log.tenantId)?.tenantId || null;
      }
      if (!tenantId) {
        // 最後從 URL 獲取
        tenantId =
          new URLSearchParams(window.location.search).get('tenantId') ||
          new URLSearchParams(window.location.search).get('tenant_id');
      }

      // 除錯模式：記錄 tenantId 狀態（但不影響發送）
      if (import.meta.env.DEV) {
        const logsWithTenantId = logsToSend.filter(log => log.tenantId).length;
        console.log(`[RemoteLogger] 準備發送 ${logsToSend.length} 條日誌`, {
          currentTenantId: this.currentTenantId,
          resolvedTenantId: tenantId,
          logsWithTenantId,
          logsWithoutTenantId: logsToSend.length - logsWithTenantId,
        });
      }

      const response = await fetch(`${apiBase}/api/logs/client`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-ID': tenantId || '', // 即使沒有 tenantId 也發送空字串
        },
        body: JSON.stringify({
          logs: logsToSend, // 照實記錄，有 tenantId 或沒有都記錄
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => '無法讀取錯誤訊息');
        throw new Error(
          `HTTP ${response.status}: ${response.statusText} - ${errorText}`
        );
      }

      const result = await response.json().catch(() => ({}));
      console.log(`[RemoteLogger] 成功上報 ${logsToSend.length} 條日誌`, {
        inserted: result.inserted,
        received: result.received,
        hasErrors: !!result.errors,
        tenantId: tenantId || 'null',
      });
    } catch (error) {
      // 使用原始 console 避免觸發遠端上報造成重試迴圈
      if (this.originalConsoleError) {
        this.originalConsoleError.call(
          console,
          '[RemoteLogger] 上報日誌失敗（請確認後端 CORS 允許本機來源）:',
          error
        );
      }
      // 如果发送失败，将日志重新加入队列（但限制队列大小）
      this.queue = [...logsToSend, ...this.queue].slice(0, this.maxQueueSize);
    } finally {
      this.isSending = false;
    }
  }

  log(level: LogEntry['level'], message: string, data?: any) {
    try {
      // 尝试获取 LINE User ID（如果 LIFF 已初始化）
      let lineUserId: string | undefined;
      try {
        // LIFF 可能还未初始化，使用 any 類型避免類型錯誤
        const liff = (window as any).liff;
        if (liff && liff.getProfile) {
          liff
            .getProfile()
            .then((profile: any) => {
              lineUserId = profile?.userId;
            })
            .catch(() => {});
        }
      } catch {
        // 忽略错误
      }

      // 優先使用設定的 tenantId，其次從 URL 獲取
      // 注意：對於 Lite 功能（有 groupId），不應該使用 tenantId
      let tenantId =
        this.currentTenantId ||
        new URLSearchParams(window.location.search).get('tenantId') ||
        new URLSearchParams(window.location.search).get('tenant_id') ||
        undefined;

      // 檢查是否為 Lite 功能（URL 中有 groupId 且為 Lite 格式：C 開頭，33 字元，無連字號）
      const groupId = new URLSearchParams(window.location.search).get(
        'groupId'
      );
      const isLiteContext =
        groupId &&
        groupId.length === 33 &&
        groupId.startsWith('C') &&
        !groupId.includes('-');

      // 如果是 Lite 功能，不設置 tenantId（避免誤用）
      if (isLiteContext) {
        tenantId = undefined;
      } else if (tenantId) {
        // 額外檢查：如果 tenantId 看起來像是 groupId（C 開頭，33 字元），則不設置
        if (
          tenantId.length === 33 &&
          tenantId.startsWith('C') &&
          !tenantId.includes('-')
        ) {
          tenantId = undefined; // 這是 groupId，不是 tenantId
        }
      }

      const entry: LogEntry = {
        level,
        message: message.substring(0, 5000), // 限制消息长度
        data: data ? this.sanitizeData(data) : undefined,
        timestamp: new Date().toISOString(),
        url: window.location.href.substring(0, 1000), // 限制 URL 长度
        userAgent: navigator.userAgent.substring(0, 500), // 限制 userAgent 长度
        tenantId,
        lineUserId,
      };

      // 添加到队列
      this.queue.push(entry);

      // 發送策略：
      // 1. 錯誤日誌：立即發送（但防抖 0.5 秒，避免連續錯誤時頻繁請求）
      // 2. 隊列滿了：立即發送
      // 3. 一般日誌：不自動發送，只在頁面卸載或手動 flush 時發送
      //    這樣可以避免因為持續的 console.log 導致看起來像輪詢
      if (level === 'error') {
        // 錯誤日誌立即發送（但也要防抖，避免連續錯誤時頻繁請求）
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        this.debounceTimer = window.setTimeout(() => {
          if (this.queue.length > 0) {
            this.sendLogs();
          }
        }, 500); // 錯誤日誌 0.5 秒後發送
      } else if (this.queue.length >= this.maxQueueSize) {
        // 隊列滿了，立即發送
        this.sendLogs();
      }
      // 一般日誌不自動發送，只在頁面卸載時發送
      // 這樣可以避免因為持續的 console.log 導致看起來像輪詢
    } catch (error) {
      // 如果日志记录本身出错，不要影响应用
      console.error('[RemoteLogger] 記錄日誌時發生錯誤:', error);
    }
  }

  private sanitizeData(data: any): any {
    try {
      // 深拷贝并处理循环引用
      const seen = new WeakSet();
      return JSON.parse(
        JSON.stringify(data, (key, value) => {
          if (typeof value === 'object' && value !== null) {
            if (seen.has(value)) {
              return '[Circular]';
            }
            seen.add(value);
          }
          // 限制对象深度
          if (key && key.length > 50) {
            return '[Key too long]';
          }
          if (typeof value === 'string' && value.length > 1000) {
            return value.substring(0, 1000) + '...[truncated]';
          }
          return value;
        })
      );
    } catch {
      return '[Unable to serialize]';
    }
  }

  // 立即发送所有日志
  async flush() {
    if (this.queue.length > 0) {
      await this.sendLogs();
    }
  }

  // 启动日誌上報（不進行定時輪詢，只在有日誌時才發送）
  start() {
    // 檢查是否應該啟用日誌上報
    const enableRemoteLogging =
      import.meta.env.VITE_ENABLE_REMOTE_LOGGING !== 'false';
    const isDevelopment =
      import.meta.env.MODE === 'development' || import.meta.env.DEV === true;

    // 在開發環境中，如果沒有明確啟用，則禁用日誌上報
    if (isDevelopment && !enableRemoteLogging) {
      console.log(
        '[RemoteLogger] 開發環境中已禁用遠程日誌上報（設定 VITE_ENABLE_REMOTE_LOGGING=true 以啟用）'
      );
      return;
    }

    // 不進行定時輪詢，只在有日誌時才發送（透過 log() 方法觸發）

    // 页面卸载前发送所有日志
    if (!this.flushOnUnload) {
      this.flushOnUnload = true;
      window.addEventListener('beforeunload', () => {
        // 使用 sendBeacon 确保在页面卸载时也能发送
        if (navigator.sendBeacon && this.queue.length > 0) {
          const apiBase = import.meta.env.VITE_API_BASE_URL;
          if (apiBase) {
            const blob = new Blob(
              [
                JSON.stringify({
                  logs: this.queue,
                  timestamp: new Date().toISOString(),
                }),
              ],
              { type: 'application/json' }
            );
            navigator.sendBeacon(`${apiBase}/api/logs/client`, blob);
          }
        } else {
          // 同步发送（可能阻塞页面卸载，但能确保发送）
          this.flush();
        }
      });

      // 页面隐藏时也发送（移动设备切换应用时）
      // 但只在有日誌時才發送，避免空請求
      document.addEventListener('visibilitychange', () => {
        if (document.hidden && this.queue.length > 0) {
          this.sendLogs();
        }
      });
    }
  }

  stop() {
    // 清除防抖計時器
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }
}

const remoteLogger = new RemoteLogger();

// 保存原始的 console 方法
const originalLog = console.log;
const originalError = console.error;
const originalWarn = console.warn;
const originalInfo = console.info;

remoteLogger.setOriginalConsoleError(originalError);

// 重写 console 方法
console.log = (...args: any[]) => {
  originalLog.apply(console, args);
  const message = args
    .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');
  remoteLogger.log('log', message, args.length > 1 ? args : undefined);
};

console.error = (...args: any[]) => {
  originalError.apply(console, args);
  const message = args
    .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');
  remoteLogger.log('error', message, args.length > 1 ? args : undefined);
};

console.warn = (...args: any[]) => {
  originalWarn.apply(console, args);
  const message = args
    .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');
  remoteLogger.log('warn', message, args.length > 1 ? args : undefined);
};

console.info = (...args: any[]) => {
  originalInfo.apply(console, args);
  const message = args
    .map(arg => (typeof arg === 'object' ? JSON.stringify(arg) : String(arg)))
    .join(' ');
  remoteLogger.log('info', message, args.length > 1 ? args : undefined);
};

// 启动日志上报（根據環境變數決定是否啟用）
// 在開發環境中，預設禁用遠程日誌上報，避免產生過多請求
// 設定 VITE_ENABLE_REMOTE_LOGGING=true 以在開發環境中啟用
remoteLogger.start();

export { remoteLogger };
